<script setup>
import { onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { updatePermissions } from '@/libs/utils/permissions.js'
import { formatDate } from '@/libs/utils/date.js'
import { getUserByReference, getUserByReferenceAndVr, searchUsers } from '@/services/userService.js'
import { getDenominationFromUser } from '@/libs/utils/users.js'
import { getCompiledPoll } from '@/services/compiledPollService.js'

import ButtonTornaAllaLista from '@/components/Generics/Buttons/ButtonTornaAllaLista.vue'
import ButtonSalva from '@/components/Generics/Buttons/ButtonSalva.vue'
import ModalSelectDelegati from '@/components/DefaultVr/Common/Modals/ModalSelectDelegati.vue'
import Loading from '@/components/Generics/Loadings/Loading.vue'

import { client } from '@/services/fetch.js'
import { toast } from '@/services/toast.js'
import TranslationProvider from '@/provider/translations.js'

const route = useRoute()
const router = useRouter()
const props = defineProps(['updatePageTitle'])
const isLoading = ref(true)

const compiledPoll = defineModel('compiledPoll')
const dateFrom = defineModel('date-from')
const dateTo = defineModel('date-to')
const dataStipula = defineModel('data-stipula')
const dataInizioPrevista = defineModel('data-inizio-prevista')
const dataConclusionePrevista = defineModel('data-conclusione-prevista')
const selected_REAdelegated = defineModel('selected_REAdelegated')
const selected_REdelegated = defineModel('selected_REdelegated')
const note = defineModel('note')

const removed_REAdelegated = ref(null)
const removed_REdelegated = ref(null)
const userList = ref([])
const currentSupervisoreValutazione = ref(null)
const currentValutatore = ref(null)
const modalSelectREA_delegated = ref({
  open: false
})
const modalSelectRE_delegated = ref({
  open: false
})
const loadingSearchUser = ref(false)
const nameSelectedREA = ref('')
const nameSelectedRE = ref('')

const dataValutazione = defineModel('dataValutazione')
const dataApprovazione = defineModel('dataApprovazione')

const utenteDataValutazione = defineModel('utenteDataValutazione')
const utenteDataApprovazione = defineModel('utenteDataApprovazione')

onMounted(async () => {
  try {
    compiledPoll.value = await getCompiledPoll(route.params.id)
  } catch (error) {
    if(error.data.status === 403) {
      router.replace({ path: '/admin/error_403' })
    }

    if(error.data.status === 404) {
      router.replace({ path: '/admin/error_404' })
    }
  }

  let pageTitle = 'Proprietà Questionario - Contratto ' + compiledPoll.value.attributes['gec.uuid']
  props.updatePageTitle(pageTitle)

  dateFrom.value = compiledPoll.value.attributes['gec.date.execution.start_date']
  dateTo.value = compiledPoll.value.attributes['gec.date.execution.conclusion_date']
  dataStipula.value = formatDate(compiledPoll.value.attributes['gec.date.stipulation.date'])
  dataInizioPrevista.value = formatDate(compiledPoll.value.attributes['gec.date.execution.expected_start_date'])
  dataConclusionePrevista.value = formatDate(compiledPoll.value.attributes['gec.date.execution.expected_conclusion_date'])
  note.value = compiledPoll.value.attributes['polls.note'] ?? ''

  dataValutazione.value = formatDate(compiledPoll.value.attributes['polls.to_be_compiled_2_confirmed.evaluated_date'])
  dataApprovazione.value = formatDate(compiledPoll.value.attributes['polls.confirmed_2_approved.approval_date'])
  utenteDataValutazione.value = compiledPoll.value.attributes['polls.to_be_compiled_2_confirmed.evaluated_date.user.denomination']
  utenteDataApprovazione.value = compiledPoll.value.attributes['polls.confirmed_2_approved.approval_date.user.denomination']

  let supervisoriValutazioneDelegati = compiledPoll.value.attributes['gec.involved_subject.rating_evaluation_approver.delegated']?.split('|') ?? []
  selected_REAdelegated.value = supervisoriValutazioneDelegati.length === 1 && supervisoriValutazioneDelegati[0] === '' ? [] : supervisoriValutazioneDelegati

  if (selected_REAdelegated.value[0]) {
    let user = await getUserByReference(selected_REAdelegated.value[0])
    nameSelectedREA.value = getDenominationFromUser(user)
  }

  let valutatoriDelegati = compiledPoll.value.attributes['gec.involved_subject.rating_evaluator.delegated']?.split('|') ?? []
  selected_REdelegated.value = valutatoriDelegati.length === 1 && valutatoriDelegati[0] === '' ? [] : valutatoriDelegati

  if (selected_REdelegated.value[0]) {
    let user = await getUserByReference(selected_REdelegated.value[0])
    nameSelectedRE.value = getDenominationFromUser(user)
  }

  currentSupervisoreValutazione.value = compiledPoll.value.attributes['gec.involved_subject.rating_evaluation_approver']
  currentValutatore.value = compiledPoll.value.attributes['gec.involved_subject.rating_evaluator']

  isLoading.value = false
})

function showPicker(e) {
  e.target.showPicker()
}

function cleanSelectedCC() {
  removed_REAdelegated.value = selected_REAdelegated.value
  selected_REAdelegated.value = []
  nameSelectedREA.value = ''
}

function cleanSelectedDT() {
  removed_REdelegated.value = selected_REdelegated.value
  selected_REdelegated.value = []
  nameSelectedRE.value = ''
}

const getUsers = async (fullSearch = '') => {
  if (fullSearch.length < 3) {
    return
  }

  loadingSearchUser.value = true

  const response = await searchUsers('default-vr', {
    fullSearch: fullSearch,
    re_ref: currentValutatore.value,
    rea_ref: currentSupervisoreValutazione.value
  })

  userList.value = response.data
  loadingSearchUser.value = false
}

async function selezionaSupervisoreValutazione(refSelezionato, refRimosso) {
  removed_REAdelegated.value = []
  removed_REAdelegated.value.push(refRimosso)

  selected_REAdelegated.value = []
  selected_REAdelegated.value.push(refSelezionato)

  modalSelectRE_delegated.value.open = false

  let user = await getUserByReferenceAndVr(selected_REAdelegated.value[0], 'default-vr')
  nameSelectedREA.value = getDenominationFromUser(user)

  modalSelectREA_delegated.value.open = false
}

async function selezionaValutatore(refSelezionato, refRimosso) {
  removed_REdelegated.value = []
  removed_REdelegated.value.push(refRimosso)

  selected_REdelegated.value = []
  selected_REdelegated.value.push(refSelezionato)

  modalSelectRE_delegated.value.open = false

  let user = await getUserByReferenceAndVr(selected_REdelegated.value[0], 'default-vr')
  nameSelectedRE.value = getDenominationFromUser(user)
}

const submitForm = async () => {
  isLoading.value = true

  try {
    updatePermissions(
      compiledPoll.value,
      currentSupervisoreValutazione.value,
      currentValutatore.value,
      selected_REAdelegated.value,
      removed_REAdelegated.value,
      selected_REdelegated.value,
      removed_REdelegated.value
    )

    const payload = {
      userReference: compiledPoll.value.userReference,
      campaign: compiledPoll.value.campaign,
      answers: compiledPoll.value.answers,
      pollTemplateRevisionNumber: compiledPoll.value.pollTemplateRevisionNumber,
      permissions: compiledPoll.value.permissions,
      attributes: {
        ...compiledPoll.value.attributes,
        'gec.date.execution.start_date': dateFrom.value,
        'gec.date.execution.conclusion_date': dateTo.value,
        'gec.involved_subject.rating_evaluator.delegated': selected_REdelegated.value?.join('|') ?? '',
        'gec.involved_subject.rating_evaluator.delegated.denomination': nameSelectedRE.value,
        'gec.involved_subject.rating_evaluation_approver.delegated': selected_REAdelegated.value?.join('|') ?? '',
        'gec.involved_subject.rating_evaluation_approver.delegated.denomination': nameSelectedREA.value,
        'polls.note': note.value
      }
    }

    let url = `/compiled-poll/api/compiled-poll/${compiledPoll.value.id}?poll=1`
    await client.put(url, payload)

    removed_REdelegated.value = null
    removed_REAdelegated.value = null

    isLoading.value = false
    toast.create('Questionario aggiornato', 'success')
  } catch (error) {
    isLoading.value = false
    toast.create('Errore durante il salvataggio. Riprova più tardi.', 'error')
  }
}
</script>

<template>
  <div class="container-fluid px-0">
    <div class="card">
      <div class="card-body scrollable-body">

        <loading v-if="isLoading"></loading>

        <div class="mb-3 container-fluid px-0" v-if="!isLoading">

          <div class="row">

            <div class="col-12 col-lg-6">
              <div class="col-12 mt-3 px-0">
                <label for="data_stipula">
                  {{ TranslationProvider.getTrans('app.data_stipula') }}
                </label>
                <input
                  id="data_stipula"
                  type="date"
                  class="form-control"
                  v-model="dataStipula"
                  disabled
                  readonly
                />
              </div>

              <div class="col-12 mt-3 px-0">
                <label for="data_inizio_prevista">
                  {{ TranslationProvider.getTrans('app.data_di_inizio_prevista') }}
                </label>
                <input
                  id="data_inizio_prevista"
                  type="date"
                  class="form-control"
                  v-model="dataInizioPrevista"
                  disabled
                  readonly
                />
              </div>

              <div class="col-12 mt-3 px-0">
                <label for="data_conclusione_prevista">
                  {{ TranslationProvider.getTrans('app.data_di_conclusione_prevista') }}
                </label>
                <input
                  id="data_conclusione_prevista"
                  type="date"
                  class="form-control"
                  v-model="dataConclusionePrevista"
                  disabled
                  readonly
                />
              </div>
            </div>

            <div class="col-12 col-lg-6">

              <div class="col-12 mt-3 col-placeholder d-none d-lg-block" style="min-height: 55px"></div>

              <div class="col-12 mt-3 px-0">
                <label for="dateFrom">
                  {{ TranslationProvider.getTrans('app.data_effettiva_di_inizio_esecuzione') }}
                </label>
                <input
                  id="dateFrom"
                  type="date"
                  class="form-control"
                  v-model="dateFrom"
                  @click="showPicker"
                  :readonly="!compiledPoll['enabledActions']['settings']"
                  :disabled="!compiledPoll['enabledActions']['settings']"
                />
              </div>
              <div class="col-12 mt-3 px-0">
                <label for="dateFrom">
                  {{ TranslationProvider.getTrans('app.data_effettiva_di_fine_esecuzione') }}
                </label>
                <input
                  id="dateTo"
                  type="date"
                  class="form-control"
                  v-model="dateTo"
                  @click="showPicker"
                  :readonly="!compiledPoll['enabledActions']['settings']"
                  :disabled="!compiledPoll['enabledActions']['settings']"
                />
              </div>
            </div>

            <div class="col-12 col-lg-6 mt-3">
              <label for="selected_CCdel">
                {{ TranslationProvider.getTrans('app.supervisore_valutazione_delegato') }}
              </label>
              <div class="input-group">
                <input type="text" class="form-control"
                       readonly
                       :disabled="!currentSupervisoreValutazione || !compiledPoll['enabledActions']['editDelegatedREA'] || !compiledPoll['enabledActions']['settings']"
                       :value="nameSelectedREA">
                <button
                  v-if="selected_REAdelegated.length && compiledPoll['enabledActions']['editDelegatedREA'] && compiledPoll['enabledActions']['settings']"
                  class="btn btn-secondary"
                  @click="cleanSelectedCC">
                  {{ TranslationProvider.getTrans('app.remove') }}
                </button>
                <button class="btn btn-primary"
                        v-if="currentSupervisoreValutazione && compiledPoll['enabledActions']['editDelegatedREA'] && compiledPoll['enabledActions']['settings']"
                        @click="modalSelectREA_delegated.open = true">
                  {{ TranslationProvider.getTrans('app.seleziona') }}
                </button>
              </div>

              <modal-select-delegati
                v-if="compiledPoll['enabledActions']['editDelegatedREA'] && compiledPoll['enabledActions']['settings']"
                :show="modalSelectREA_delegated.open"
                @close="modalSelectREA_delegated.open = false"
                @search="getUsers"
                :loading="loadingSearchUser"
                :user-list="userList"
                @selected="selezionaSupervisoreValutazione"
                :title="TranslationProvider.getTrans('app.seleziona_supervisore_delegato')"
                :sel_delegated="selected_REAdelegated"
                :nome_selezionato="nameSelectedREA"
              ></modal-select-delegati>

            </div>

            <div class="col-12 col-lg-6 mt-3">
              <label for="selected_CCdel">
                {{ TranslationProvider.getTrans('app.valutatore_delegato') }}
              </label>
              <div class="input-group">
                <input type="text" class="form-control"
                       readonly
                       :disabled="!currentValutatore || !compiledPoll['enabledActions']['editDelegatedRE'] || !compiledPoll['enabledActions']['settings']"
                       :value="nameSelectedRE">
                <button
                  v-if="selected_REdelegated.length && compiledPoll['enabledActions']['editDelegatedRE'] && compiledPoll['enabledActions']['settings']"
                  class="btn btn-secondary"
                  @click="cleanSelectedDT">
                  {{ TranslationProvider.getTrans('app.remove') }}
                </button>
                <button class="btn btn-primary"
                        v-if="currentValutatore && compiledPoll['enabledActions']['editDelegatedRE'] && compiledPoll['enabledActions']['settings']"
                        @click="modalSelectRE_delegated.open = true">
                  {{ TranslationProvider.getTrans('app.seleziona') }}
                </button>
              </div>

              <modal-select-delegati
                v-if="compiledPoll['enabledActions']['editDelegatedRE'] && compiledPoll['enabledActions']['settings']"
                :show="modalSelectRE_delegated.open"
                @close="modalSelectRE_delegated.open = false"
                @search="getUsers"
                :loading="loadingSearchUser"
                :user-list="userList"
                @selected="selezionaValutatore"
                :title="TranslationProvider.getTrans('app.seleziona_valutatore_delegato')"
                :sel_delegated="selected_REdelegated"
                :nome_selezionato="nameSelectedRE"
              ></modal-select-delegati>
            </div>

            <div class="col-12 col-lg-12 mt-3">
              <label for="note">
                {{ TranslationProvider.getTrans('app.note') }}
              </label>
              <div class="input-group">
                <textarea class="form-control" id="note"
                          v-model="note"
                          :disabled="!compiledPoll['enabledActions']['settings']"></textarea>
              </div>
            </div>

          </div>

          <div class="row mt-3" v-if="dataValutazione">
            <div class="col-12 col-lg-6 mt-3 mt-lg-0">
              <label for="data_valutazione">
                {{ TranslationProvider.getTrans('app.data_valutazione') }}
              </label>
              <input
                id="data_valutazione"
                type="date"
                class="form-control"
                v-model="dataValutazione"
                disabled
                readonly
              />
            </div>
            <div class="col-12 col-lg-6 mt-3 mt-lg-0">
              <label for="utente_data_valutazione">
                {{ TranslationProvider.getTrans('app.valutato_da') }}
              </label>
              <input
                id="utente_data_valutazione"
                type="text"
                class="form-control"
                v-model="utenteDataValutazione"
                disabled
                readonly
              />
            </div>
          </div>

          <div class="row mt-3" v-if="dataApprovazione">
            <div class="col-12 col-lg-6 mt-3 mt-lg-0">
              <label for="data_valutazione">
                {{ TranslationProvider.getTrans('app.data_approvazione') }}
              </label>
              <input
                id="data_valutazione"
                type="date"
                class="form-control"
                v-model="dataApprovazione"
                disabled
                readonly
              />
            </div>
            <div class="col-12 col-lg-6 mt-3 mt-lg-0">
              <label for="utente_data_valutazione">
                {{ TranslationProvider.getTrans('app.approvato_da') }}
              </label>
              <input
                id="utente_data_valutazione"
                type="text"
                class="form-control"
                v-model="utenteDataApprovazione"
                disabled
                readonly
              />
            </div>
          </div>

          <div class="row mt-3"
               v-if="compiledPoll['attributes']['polls.to_be_compiled_2_wont_do.not_evaluated.reason']">
            <h3>Dati di non valutazione</h3>
            <div class="col-12 col-lg-6 mt-3 mt-lg-0">
              <label for="data_valutazione">Motivazione</label>
              <input
                id="reason"
                type="text"
                class="form-control"
                :value="compiledPoll['attributes']['polls.to_be_compiled_2_wont_do.not_evaluated.reason']"
                disabled
                readonly
              />
            </div>
            <div class="col-12 col-lg-6 mt-3 mt-lg-0">
              <label for="data_valutazione">Note</label>
              <input
                id="reason_note"
                type="text"
                class="form-control"
                :value="compiledPoll['attributes']['polls.to_be_compiled_2_wont_do.not_evaluated.reason.note']"
                disabled
                readonly
              />
            </div>
            <div class="col-12 col-lg-6 mt-3 mt-lg-0">
              <label for="data_valutazione">Data</label>
              <input
                id="date_not_evaluated"
                type="date"
                class="form-control"
                :value="formatDate(compiledPoll['attributes']['polls.to_be_compiled_2_wont_do.not_evaluated_date'])"
                disabled
                readonly
              />
            </div>
            <div class="col-12 col-lg-6 mt-3 mt-lg-0">
              <label for="data_valutazione">Utente</label>
              <input
                id="user_not_evaluated"
                type="text"
                class="form-control"
                :value="compiledPoll['attributes']['polls.to_be_compiled_2_wont_do.not_evaluated_date.user.denomination']"
                disabled
                readonly
              />
            </div>
          </div>

        </div>
      </div>
    </div>

    <div class="justify-content-between d-flex mt-3">
      <router-link
        :to="{ name: 'admin/DefaultVr_CompiledPoll' , query: { page: route.query.page, filters: route.query.filters} }">
        <button-torna-alla-lista></button-torna-alla-lista>
      </router-link>

      <button-salva
        v-if="compiledPoll && compiledPoll['enabledActions']['settings']"
        @click="submitForm"
        :disabled="isLoading"
        :loading="isLoading">
      </button-salva>
    </div>

  </div>
</template>

<style scoped lang="scss">

</style>
